import { Box, Flex, Spinner } from '@radix-ui/themes';
import {
  ensureInstallationDataCollections,
  reconcileInstallationOnOpen,
} from 'classes/helpers/install-data-collections.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { PresetTrainingDialogHoC } from 'components/machine/dialogs/preset-training';
import { MachineContext } from 'contexts/machine.context';
import { SessionEventsContext } from 'contexts/session-events.context';
import { VisionInstallSubStep } from 'enums/installation';
import { SettingsSection } from 'enums/settings';
import { t } from 'i18next';
import { IVisionInstallUiState } from 'interfaces/i-installation';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { TrackingDevice } from 'lib_ts/enums/training.enums';
import { Nullable, Nullish } from 'lib_ts/interfaces/common/i-generics';
import { IDataCollection } from 'lib_ts/interfaces/i-data-collection';
import { IInstallationDetails } from 'lib_ts/interfaces/i-machine';
import { IModelPrediction } from 'lib_ts/interfaces/modelling/i-eval-models';
import { IRepeatabilityResult } from 'lib_ts/interfaces/modelling/i-repeatability';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { IVisionCameraCalibration } from 'lib_ts/interfaces/vision/i-vision-camera-calibration';
import { clone, merge } from 'lodash';
import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { DataCollectionsService } from 'services/data-collections.service';
import { SessionEventsService } from 'services/session-events.service';
import { VisionCameraCalibrationsService } from 'services/vision-camera-calibrations';
import { useAuthStore } from 'stores/use-auth-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const CONTEXT_NAME = 'InstallationContext';

export enum PerformanceStepTabKey {
  DataCollection = 'DataCollection',
  Results = 'Results',
}

const DEFAULT_VISION_UI: IVisionInstallUiState = {
  subStep: VisionInstallSubStep.Start,
  distancePendingComplete: false,
};

export interface IInstallationContext {
  readOnly: boolean;

  installation: IInstallationDetails;
  readonly updateInstallation: (value: Partial<IInstallationDetails>) => void;

  modelPerformance?: IModelPrediction;
  readonly setModelPerformance: (value: IModelPrediction) => void;

  machinePerformance?: IRepeatabilityResult;
  readonly setMachinePerformance: (value: IRepeatabilityResult) => void;

  readonly tryCancel: () => void;

  btc?: IDataCollection;
  btcTab: PerformanceStepTabKey;
  readonly setBtcTab: (value: PerformanceStepTabKey) => void;

  rt?: IDataCollection;
  rtTab: PerformanceStepTabKey;
  readonly setRtTab: (value: PerformanceStepTabKey) => void;
  rtTrainingPitches?: ITrainingPitch[];
  readonly trainRtPitches: () => void;

  readonly reloadRT: () => Promise<void>;

  migrating: boolean;
  syncingBallType: boolean;
  readonly syncBallType: (newBallType: BallType) => Promise<void>;

  vcc?: IVisionCameraCalibration;

  // allows complete VCC action to update the object in this context
  setVcc: (v: IVisionCameraCalibration) => void;
  readonly reloadVcc: () => Promise<void>;

  visionUi: IVisionInstallUiState;
  readonly setVisionUi: (patch: Partial<IVisionInstallUiState>) => void;
}

const DEFAULT: IInstallationContext = {
  readOnly: false,

  installation: {
    started_by: 'not init',
    start_date: new Date().toISOString(),
    ball_type: BallType.MLB,
    ball_type_locked: false,

    btcID: null,
    end_date: null,
    finished_by: null,
    model_id: null,
    rtID: null,
    standalone: null,

    step_ball_type: null,
    step_completion: null,
    step_general_config: null,
    step_hardware_changes: null,
    step_projector_config: null,
    step_repeatability: null,
    step_test_shot: null,
    step_vision: null,

    vccID: null,
    vcdFt: null,
    vcdComplete: null,
  },

  btcTab: PerformanceStepTabKey.DataCollection,
  setBtcTab: () => console.error(`${CONTEXT_NAME}: not init`),

  rtTab: PerformanceStepTabKey.DataCollection,
  setRtTab: () => console.error(`${CONTEXT_NAME}: not init`),

  updateInstallation: () => console.error(`${CONTEXT_NAME}: not init`),

  setModelPerformance: () => console.error(`${CONTEXT_NAME}: not init`),
  setMachinePerformance: () => console.error(`${CONTEXT_NAME}: not init`),

  tryCancel: () => console.error(`${CONTEXT_NAME}: not init`),

  trainRtPitches: () => console.error(`${CONTEXT_NAME}: not init`),

  reloadRT: async () => console.error(`${CONTEXT_NAME}: not init`),

  migrating: false,
  syncingBallType: false,
  syncBallType: async () => console.error(`${CONTEXT_NAME}: not init`),

  setVcc: () => console.error(`${CONTEXT_NAME}: not init`),
  reloadVcc: async () => console.error(`${CONTEXT_NAME}: not init`),

  visionUi: DEFAULT_VISION_UI,
  setVisionUi: () => console.error(`${CONTEXT_NAME}: not init`),
};

export const InstallationContext = createContext(DEFAULT);

interface IActiveProps {
  installation: IInstallationDetails;
  onCancel: () => void;
  children: ReactNode;
}

const InstallationProviderActive: FC<IActiveProps> = (props) => {
  const { setDialogSettings } = useContext(SessionEventsContext);
  const {
    machine,
    update: updateMachine,
    createDataCollection,
  } = useContext(MachineContext);

  const current = useAuthStore((s) => s.current);

  // loaded when the user opens the relevant tab on results step
  const [modelPerformance, setModelPerformance] = useState<IModelPrediction>();
  // loaded when the user opens the relevant tab on results step
  const [machinePerformance, setMachinePerformance] =
    useState<IRepeatabilityResult>();

  const [ballTypeActiveTab, setBallTypeActiveTab] =
    useState<PerformanceStepTabKey>(DEFAULT.btcTab);
  const [repeatabilityActiveTab, setRepeatabilityActiveTab] =
    useState<PerformanceStepTabKey>(DEFAULT.rtTab);

  const [dialogCancel, setDialogCancel] = useState<number>();

  const updateInstallation = useCallback(
    async (value: Partial<IInstallationDetails>) => {
      const nextValue = clone(props.installation);

      merge(nextValue, value);

      await updateMachine({
        last_installation: nextValue,
      });

      if (value.end_date) {
        SessionEventsService.createOne({
          category: 'install',
          tags: 'ended',
          data: nextValue,
        });
      }
    },
    [props.installation, updateMachine]
  );

  const [rt, setRT] = useState<IDataCollection>();
  const [rtTrainingPitches, setRtTrainingPitches] = useState<ITrainingPitch[]>(
    []
  );

  // load the RT collection, initialize the training pitches

  const isAdmin = current.role === UserRole.admin;

  const migrationRan = useRef(false);
  const collectionsLoaded = useRef({ btc: false, rt: false });

  const loadRT = async (id: Nullable<string>) => {
    if (!id) {
      setRT(undefined);
      setRtTrainingPitches([]);
      collectionsLoaded.current.rt = true;
      return;
    }

    const collection = await DataCollectionsService.getOne(id);
    setRT(collection);

    // initialize training pitches
    if (!collection?.pitches) {
      setRtTrainingPitches([]);
      collectionsLoaded.current.rt = true;
      return;
    }

    // find all the pitches that are not precision trained (yet)
    const npPitches = collection.pitches.filter(
      (p) => !p._mx_ms.precision_trained
    );

    const breakPitches = npPitches.filter(
      (p) => p.priority === BuildPriority.Breaks
    );

    const hasSpinPitches = breakPitches.length < npPitches.length;
    const onVision = machine.tracking_device === TrackingDevice.TrajektVision;
    const onTrackman = machine.tracking_device === TrackingDevice.TrackmanB1;

    const onBreakOnlyDevice = (onVision && !isAdmin) || onTrackman;

    // vision & trackman currently only support Break priority pitches
    if (hasSpinPitches && onBreakOnlyDevice) {
      // todo - show this warning after the user actually clicks "Train x Pitches"?
      NotifyHelper.warning({
        message_md: onVision
          ? t('common.vision-training-no-spin')
          : t('common.trackman-training-no-spin'),
        buttons: [
          {
            label: 'common.open-settings',
            onClick: () => {
              setDialogSettings({
                identifier: 'BreakOnlyDeviceSettings-DataCollector',
                initialSection: SettingsSection.Machine,
                onClose: () => setDialogSettings(undefined),
              });
            },
            dismissAfterClick: true,
          },
        ],
      });

      setRtTrainingPitches(breakPitches);
      collectionsLoaded.current.rt = true;
      return;
    }

    setRtTrainingPitches(npPitches);
    collectionsLoaded.current.rt = true;
  };

  const [btc, setBTC] = useState<IDataCollection>();
  const [migrating, setMigrating] = useState(false);
  const [syncingBallType, setSyncingBallType] = useState(false);

  // load the RT collection, initialize the training pitches
  useEffect(() => {
    if (props.installation.end_date) {
      setRT(undefined);
      setRtTrainingPitches([]);
      collectionsLoaded.current.rt = true;
      return;
    }

    collectionsLoaded.current.rt = false;
    loadRT(props.installation.rtID);
  }, [props.installation.rtID, props.installation.end_date]);

  useEffect(() => {
    if (props.installation.end_date) {
      setBTC(undefined);
      return;
    }

    if (!props.installation.btcID) {
      setBTC(undefined);
      collectionsLoaded.current.btc = true;
      return;
    }

    collectionsLoaded.current.btc = false;
    DataCollectionsService.getOne(props.installation.btcID).then((result) => {
      setBTC(result);
      collectionsLoaded.current.btc = true;
    });
  }, [props.installation.btcID, props.installation.end_date]);

  useEffect(() => {
    if (migrationRan.current) {
      return;
    }

    if (props.installation.end_date || props.installation.standalone) {
      return;
    }

    const btcReady = !props.installation.btcID || collectionsLoaded.current.btc;
    const rtReady = !props.installation.rtID || collectionsLoaded.current.rt;

    if (!btcReady || !rtReady) {
      return;
    }

    migrationRan.current = true;

    const runMigration = async () => {
      setMigrating(true);

      try {
        await reconcileInstallationOnOpen({
          installation: props.installation,
          machineID: machine.machineID,
          btc,
          rt,
          createDataCollection,
          updateInstallation,
        });
      } finally {
        setMigrating(false);
      }
    };

    runMigration();
  }, [
    btc,
    rt,
    props.installation,
    machine.machineID,
    createDataCollection,
    updateInstallation,
  ]);

  const syncBallType = useCallback(
    async (newBallType: BallType) => {
      if (props.installation.ball_type_locked) {
        return;
      }

      if (newBallType === props.installation.ball_type) {
        return;
      }

      setSyncingBallType(true);

      try {
        const saved = await updateMachine(
          {
            _id: machine._id,
            ball_type: newBallType,
            last_installation: {
              ...props.installation,
              ball_type: newBallType,
            },
          },
          true
        );

        if (!saved) {
          return;
        }

        const result = await ensureInstallationDataCollections({
          machineID: machine.machineID,
          ballType: newBallType,
          installation: {
            ...props.installation,
            ball_type: newBallType,
          },
          createDataCollection,
          existingBtc: btc,
          existingRt: rt,
        });

        const patch: Partial<IInstallationDetails> = {
          ball_type: newBallType,
          btcID: result.btcID,
          rtID: result.rtID,
        };

        if (result.recreatedBtc || result.recreatedRt) {
          patch.model_id = null;
        }

        await updateInstallation(patch);
      } finally {
        setSyncingBallType(false);
      }
    },
    [
      props.installation,
      machine._id,
      machine.machineID,
      btc,
      rt,
      createDataCollection,
      updateMachine,
      updateInstallation,
    ]
  );

  const [vcc, setVcc] = useState<IVisionCameraCalibration>();
  const [visionUi, setVisionUiState] =
    useState<IVisionInstallUiState>(DEFAULT_VISION_UI);

  const setVisionUi = useCallback((patch: Partial<IVisionInstallUiState>) => {
    setVisionUiState((prev) => ({ ...prev, ...patch }));
  }, []);

  const reloadVcc = useCallback(async () => {
    if (props.installation.end_date || !props.installation.vccID) {
      setVcc(undefined);
      return;
    }

    const result = await VisionCameraCalibrationsService.getInstance().getOne(
      props.installation.vccID
    );

    setVcc(result);
  }, [props.installation.end_date, props.installation.vccID]);

  useEffect(() => {
    if (props.installation.end_date) {
      setVisionUiState(DEFAULT_VISION_UI);
    }
  }, [props.installation.end_date]);

  // load up the VCC based on the input ID
  useEffect(() => {
    if (props.installation.end_date) {
      setVcc(undefined);
      return;
    }

    if (!props.installation.vccID) {
      setVcc(undefined);
      return;
    }

    VisionCameraCalibrationsService.getInstance()
      .getOne(props.installation.vccID)
      .then((result) => setVcc(result));
  }, [props.installation.vccID, props.installation.end_date]);

  const [dialogTrainRT, setDialogTrainRT] = useState<number>();

  const state: IInstallationContext = {
    readOnly: !!props.installation.end_date,

    installation: props.installation,
    updateInstallation: updateInstallation,

    modelPerformance: modelPerformance,
    setModelPerformance: setModelPerformance,

    machinePerformance: machinePerformance,
    setMachinePerformance: (v) =>
      setMachinePerformance({ ...v, datestamp: new Date() }),

    btcTab: ballTypeActiveTab,
    setBtcTab: setBallTypeActiveTab,

    rtTab: repeatabilityActiveTab,
    setRtTab: setRepeatabilityActiveTab,

    tryCancel: () => setDialogCancel(Date.now()),

    btc: btc,

    rt: rt,
    rtTrainingPitches: rtTrainingPitches,
    trainRtPitches: () => setDialogTrainRT(Date.now()),

    reloadRT: () => loadRT(props.installation.rtID),

    migrating,
    syncingBallType,
    syncBallType,

    vcc: vcc,
    setVcc: setVcc,
    reloadVcc,

    visionUi,
    setVisionUi,
  };

  return (
    <InstallationContext.Provider value={state}>
      {migrating ? (
        <Flex align="center" justify="center" p="6" gap="3">
          <Spinner />
          <Box>Preparing installation...</Box>
        </Flex>
      ) : (
        props.children
      )}

      {dialogCancel && (
        <CommonConfirmationDialog
          key={dialogCancel}
          identifier="InstallationCancelConfirm"
          title="common.warning"
          content={
            <Box>
              This will quit the process and all data will be lost. Do you want
              to continue?
            </Box>
          }
          action={{
            label: 'common.continue',
            color: RADIX.COLOR.DANGER,
            onClick: async () => {
              if (btc && !btc.complete && !btc.cancelled) {
                await DataCollectionsService.updateOne(btc._id, {
                  cancelled: true,
                });
              }

              if (rt && !rt.complete && !rt.cancelled) {
                await DataCollectionsService.updateOne(rt._id, {
                  cancelled: true,
                });
              }

              await updateInstallation({
                end_date: new Date().toISOString(),
              });
              setDialogCancel(undefined);
              props.onCancel();
            },
          }}
          cancel={{
            label: 'common.close',
            onClick: () => {
              setDialogCancel(undefined);
            },
          }}
        />
      )}

      {dialogTrainRT && (
        <PresetTrainingDialogHoC
          key={dialogTrainRT}
          identifier="RT-TrainingDialog"
          pitches={rtTrainingPitches}
          onClose={() => {
            // trigger the listener to wait for machine to stop firing > lower the machine if necessary
            useTrainedMsStore.setState({
              lastTrainingEnded: Date.now(),
            });

            setDialogTrainRT(undefined);

            if (!rt) {
              // shouldn't trigger
              return;
            }

            // remove any precision trained pitches from the remaining list
            setRtTrainingPitches((prev) =>
              prev.filter((p) => !p._mx_ms.precision_trained)
            );
          }}
          updateTrainingPitch={async (payload) => {
            console.assert(
              !!payload._mx_ms,
              `Installation > RT > PT > updateTrainingPitch should have _mx_ms value`
            );

            // keep track of the change for training pitches
            setRtTrainingPitches((prev) =>
              prev.map((p) => {
                if (p._id !== payload._id) {
                  return p;
                }

                const nextPitch = clone(p);
                merge(nextPitch, payload);
                return nextPitch;
              })
            );

            // update the collectioin's pitch with the precision ms
            if (!rt?.pitches) {
              // shouldn't trigger
              return;
            }

            const nextRT = await DataCollectionsService.updateOne(rt._id, {
              // kind of overkill but it looks more consistent
              pitches: rt.pitches.map((p) => {
                if (p._id !== payload._id) {
                  return p;
                }

                const nextPitch = clone(p);
                merge(nextPitch, payload);
                return nextPitch;
              }),
            });

            setRT(nextRT);
          }}
        />
      )}
    </InstallationContext.Provider>
  );
};

interface IProps {
  installation: Nullish<IInstallationDetails>;
  onCancel: () => void;
  children: ReactNode;
}

export const InstallationProvider: FC<IProps> = ({
  installation,
  onCancel,
  children,
}) => {
  if (!installation) {
    return <>{children}</>;
  }

  return (
    <InstallationProviderActive installation={installation} onCancel={onCancel}>
      {children}
    </InstallationProviderActive>
  );
};
